/**
 * Ad attribution captured from the landing-page link, against the contract the service accepts
 * (`@nrx/betty-contracts/attribution`). Values that fail are dropped one by one here, so a bad
 * `utm_term` never costs us the click id. Persisted in localStorage — non-PHI, this device only.
 */

import {
  AD_ATTRIBUTION_CLICK_ID_RE,
  AD_ATTRIBUTION_CLICK_TYPES,
  AD_ATTRIBUTION_LANDING_PATH_RE,
  AD_ATTRIBUTION_NRX_LP_RE,
  AD_ATTRIBUTION_UTM_KEYS,
  AD_ATTRIBUTION_UTM_RE,
  type FfsAdAttribution,
  parseAdAttribution,
} from '@nrx/betty-contracts/attribution';

export type Attribution = FfsAdAttribution;

export const ATTRIBUTION_STORAGE_KEY = 'ffs.attribution.v1';

const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

let memory: Attribution | undefined;

export function resetAttributionForTests(): void {
  memory = undefined;
}

function newer(a: Attribution | undefined, b: Attribution | undefined): Attribution | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Date.parse(a.capturedAt) >= Date.parse(b.capturedAt) ? a : b;
}

/**
 * The later of the stored and in-memory touches. Memory can be newer than storage when `getItem`
 * works but `setItem` throws (a full quota), and last touch must still win. A stored touch that
 * fails the contract — another app version, another tab, an extension, tampering — is discarded
 * rather than sent: the service drops an invalid attribution whole, so forwarding one would
 * silently lose the visit.
 */
function readStored(): Attribution | undefined {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (raw === null) return memory;
    const parsed: unknown = JSON.parse(raw);
    return newer(memory, parseAdAttribution(parsed));
  } catch {
    return memory;
  }
}

function write(touch: Attribution | undefined): void {
  memory = touch;
  try {
    if (touch === undefined) localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
    else localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // Storage blocked (private mode, disabled site data): the in-memory copy covers this page load.
  }
}

function isExpired(touch: Attribution, now: Date): boolean {
  const capturedAt = Date.parse(touch.capturedAt);
  // An unparseable timestamp can never age out, so it counts as expired rather than live forever.
  return Number.isNaN(capturedAt) || now.getTime() - capturedAt >= WINDOW_MS;
}

function parse(params: URLSearchParams, pathname: string, now: Date): Attribution | undefined {
  const utm: Record<string, string> = {};
  for (const key of AD_ATTRIBUTION_UTM_KEYS) {
    const value = params.get(`utm_${key}`)?.trim();
    if (value !== undefined && AD_ATTRIBUTION_UTM_RE.test(value)) utm[key] = value;
  }
  const nrxLp = params.get('nrx_lp')?.trim();
  const clickType = AD_ATTRIBUTION_CLICK_TYPES.find((k) =>
    AD_ATTRIBUTION_CLICK_ID_RE.test(params.get(k) ?? ''),
  );
  const clickId = clickType !== undefined ? (params.get(clickType) as string) : undefined;

  const hasUtm = Object.keys(utm).length > 0;
  const validLp = nrxLp !== undefined && AD_ATTRIBUTION_NRX_LP_RE.test(nrxLp);
  if (!hasUtm && !validLp && clickType === undefined) return undefined;

  return {
    v: 1,
    capturedAt: now.toISOString(),
    ...(AD_ATTRIBUTION_LANDING_PATH_RE.test(pathname) ? { landingPath: pathname } : {}),
    ...(validLp ? { nrxLp } : {}),
    ...(hasUtm ? { utm } : {}),
    ...(clickType !== undefined && clickId !== undefined
      ? {
          click: {
            type: clickType,
            id: clickId,
            // Meta wants the click's arrival time, not the conversion's.
            ...(clickType === 'fbclid' ? { fbc: `fb.1.${now.getTime()}.${clickId}` } : {}),
          },
        }
      : {}),
  };
}

/**
 * Runs once at boot, before render — before the Stripe-resume path's `history.replaceState` wipes
 * the query string. Never mutates the URL. A tagged arrival replaces the stored touch; an untagged
 * one keeps it until it expires.
 */
export function captureAttribution(
  location: { search: string; pathname: string },
  now: Date = new Date(),
): void {
  const touch = parse(new URLSearchParams(location.search), location.pathname, now);
  if (touch !== undefined) {
    write(touch);
    return;
  }
  const stored = readStored();
  if (stored !== undefined && isExpired(stored, now)) write(undefined);
}

export function getAttribution(now: Date = new Date()): Attribution | undefined {
  const stored = readStored();
  return stored !== undefined && !isExpired(stored, now) ? stored : undefined;
}
